import React, { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { api, ApiError } from "../../lib/api"
import { formatClock, formatDateTime } from "../../lib/format"
import { useAttemptTimer } from "../../hooks/useCountdown"
import { BrandLogo } from "../ui/BrandLogo"
import { Button } from "../ui/Button"
import { Modal } from "../ui/Modal"
import { PageLoading } from "../ui/Spinner"
import { useToast } from "../ui/Toast"
import QuestionCard from "../QuestionCard"
import type { AnswerResponse, AttemptInProgress, AttemptPayload, AttemptSummary } from "../../types"

type SaveState = "idle" | "saving" | "saved" | "error"

// Pausa entre marcar a alternativa e pular para a próxima, com "Avançar ao
// responder" ligado. Curto o bastante para não travar o ritmo, longo o bastante
// para o candidato ver que a resposta pegou.
const AUTO_ADVANCE_MS = 250

function isInProgress(a: AttemptPayload): a is AttemptInProgress {
  return a.status === "in_progress" && Array.isArray((a as AttemptInProgress).questions)
}

interface QuestionNavProps {
  questions: AttemptInProgress["questions"]
  answers: Record<number, number | null>
  flagged: Set<number>
  current: number
  onJump: (idx: number) => void
  variant: "grid" | "strip"
}

/** Mapa de questões — sempre visível (grid na sidebar do desktop, faixa rolável no mobile). */
function QuestionNav({ questions, answers, flagged, current, onJump, variant }: QuestionNavProps) {
  return (
    <div
      className={variant === "grid" ? "grid grid-cols-5 gap-2" : "flex gap-2 overflow-x-auto pb-1"}
      role="listbox"
      aria-label="Ir para questão"
    >
      {questions.map((q, idx) => {
        const answered = (answers[q.position] ?? null) !== null
        const isCur = idx === current
        const isFlag = flagged.has(q.position)
        return (
          <button
            key={q.position}
            type="button"
            onClick={() => onJump(idx)}
            aria-current={isCur ? "true" : undefined}
            aria-label={`Questão ${q.position}${answered ? ", respondida" : ", sem resposta"}${isFlag ? ", marcada para revisão" : ""}`}
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold tabular-nums transition-colors ${
              answered ? "bg-navy-800 text-white hover:bg-navy-900" : "bg-slate-200 text-navy-600 hover:bg-slate-300"
            } ${isFlag ? "ring-2 ring-gold-400" : ""} ${isCur ? "outline outline-2 outline-navy-900 outline-offset-2" : ""}`}
          >
            {q.position}
          </button>
        )
      })}
    </div>
  )
}

export default function Simulado() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [attempt, setAttempt] = useState<AttemptInProgress | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [current, setCurrent] = useState(0)
  const [answers, setAnswers] = useState<Record<number, number | null>>({})
  const [flagged, setFlagged] = useState<Set<number>>(new Set())
  const [saveState, setSaveState] = useState<SaveState>("idle")
  const [autoAdvance, setAutoAdvance] = useState(false)
  const [finishModalOpen, setFinishModalOpen] = useState(false)
  const [finishing, setFinishing] = useState(false)

  const finishingRef = useRef(false)
  const saveSeq = useRef(0)
  const advanceTimer = useRef<number | null>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const skipScroll = useRef(true)

  const resultPath = `/simulado/${id}/resultado`

  const goToResult = useCallback(() => {
    navigate(resultPath, { replace: true })
  }, [navigate, resultPath])

  const cancelAutoAdvance = useCallback(() => {
    if (advanceTimer.current !== null) {
      window.clearTimeout(advanceTimer.current)
      advanceTimer.current = null
    }
  }, [])

  // Toda troca de questão passa por aqui: cancela um auto-avanço pendente para
  // que a navegação manual (mapa, Próxima/Anterior) nunca seja atropelada por
  // um avanço agendado na questão que o usuário acabou de deixar.
  const goTo = useCallback(
    (idx: number) => {
      cancelAutoAdvance()
      setCurrent(idx)
    },
    [cancelAutoAdvance]
  )

  useEffect(() => cancelAutoAdvance, [cancelAutoAdvance])

  const finishAttempt = useCallback(
    async (auto: boolean) => {
      if (finishingRef.current) return
      finishingRef.current = true
      setFinishing(true)
      if (auto) toast("Tempo esgotado! Finalizando seu simulado...", "info")
      try {
        await api.post<{ attempt: AttemptSummary }>(`/api/attempts/${id}/finish`)
      } catch {
        // 409 attempt_closed ou rede: o resultado resolve o estado real
      }
      goToResult()
    },
    [id, goToResult, toast]
  )

  const { remaining, sync } = useAttemptTimer(attempt ? attempt.remaining_seconds : null, () => {
    void finishAttempt(true)
  })

  // Carregar / retomar
  useEffect(() => {
    let cancelled = false
    api
      .get<{ attempt: AttemptPayload }>(`/api/attempts/${id}`)
      .then(({ attempt: a }) => {
        if (cancelled) return
        if (!isInProgress(a)) {
          navigate(`/simulado/${a.id}/resultado`, { replace: true })
          return
        }
        const initial: Record<number, number | null> = {}
        a.questions.forEach((q) => {
          initial[q.position] = q.given_answer
        })
        setAnswers(initial)
        setAttempt(a)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setLoadError(e instanceof ApiError ? e.message : "Erro ao carregar o simulado")
      })
    return () => {
      cancelled = true
    }
  }, [id, navigate])

  // Trocar de questão sem levar o scroll junto faz a troca passar despercebida:
  // o candidato responde no fim da página e continua lá, olhando as alternativas
  // da questão seguinte — parece que o toque não pegou. Sobe até o topo do card,
  // descontando a barra fixa. Não roda no primeiro render (retomar um simulado
  // não deve mexer na página).
  useEffect(() => {
    if (skipScroll.current) {
      skipScroll.current = false
      return
    }
    const card = cardRef.current
    if (!card) return
    const offset = (headerRef.current?.offsetHeight ?? 0) + 12
    const top = Math.max(0, card.getBoundingClientRect().top + window.scrollY - offset)
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    window.scrollTo({ top, behavior: reduce ? "auto" : "smooth" })
  }, [current])

  const question = attempt?.questions[current] ?? null

  async function selectAnswer(altIndex: number) {
    if (!attempt || !question || finishingRef.current) return
    const prev = answers[question.position] ?? null
    const next = prev === altIndex ? null : altIndex
    setAnswers((a) => ({ ...a, [question.position]: next }))
    // Sempre cancela o avanço pendente: se o toque foi para desmarcar, ou para
    // trocar de alternativa, o relógio recomeça (ou não é agendado).
    cancelAutoAdvance()
    const last = current >= attempt.total_questions - 1
    if (autoAdvance && next !== null && !last) {
      // O respiro deixa a alternativa marcada aparecer antes da troca — avançar
      // no mesmo frame do toque faz parecer que a resposta não foi registrada.
      advanceTimer.current = window.setTimeout(() => {
        advanceTimer.current = null
        if (finishingRef.current) return
        setCurrent((c) => Math.min(attempt.total_questions - 1, c + 1))
      }, AUTO_ADVANCE_MS)
    }
    setSaveState("saving")
    const seq = ++saveSeq.current
    try {
      const res = await api.post<AnswerResponse>(`/api/attempts/${attempt.id}/answer`, {
        position: question.position,
        given_answer: next,
      })
      if (seq === saveSeq.current) {
        setSaveState("saved")
        sync(res.remaining_seconds)
      }
    } catch (e) {
      if (e instanceof ApiError && e.code === "attempt_closed") {
        toast("Tempo esgotado", "info")
        goToResult()
        return
      }
      if (seq === saveSeq.current) setSaveState("error")
    }
  }

  function toggleFlag() {
    if (!question) return
    setFlagged((f) => {
      const nf = new Set(f)
      if (nf.has(question.position)) nf.delete(question.position)
      else nf.add(question.position)
      return nf
    })
  }

  if (loadError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-100 px-6 text-center">
        <p className="text-navy-700">{loadError}</p>
        <Button variant="secondary" onClick={() => navigate("/dashboard")}>
          Voltar ao início
        </Button>
      </div>
    )
  }

  if (!attempt || !question) return <PageLoading label="Preparando seu simulado..." />

  const total = attempt.total_questions
  const answeredCount = Object.values(answers).filter((v) => v !== null).length
  const unanswered = attempt.questions.filter((q) => (answers[q.position] ?? null) === null)
  const selected = answers[question.position] ?? null
  const isFlagged = flagged.has(question.position)
  const isLast = current === total - 1

  const secs = remaining ?? attempt.remaining_seconds
  // Cronômetro visível só no modo Oficial (decisão 3.3) — Curta/Média têm
  // prazo server-side (24h), mas sem pressão de tempo na tela.
  const showTimer = attempt.mode === "oficial"
  const timerColor = secs < 60 ? "text-red-600" : secs < 300 ? "text-amber-600" : "text-navy-900"
  const progressPct = total > 0 ? Math.round((answeredCount / total) * 100) : 0

  return (
    <div className="min-h-screen bg-slate-100 text-navy-900">
      {/* Barra superior */}
      <div ref={headerRef} className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 lg:px-6">
          <div className="min-w-0">
            <BrandLogo to={null} compact />
            <p className="mt-1 truncate text-xs text-navy-400">Iniciado em {formatDateTime(attempt.started_at)}</p>
          </div>
          {/* Mobile: timer (só no Oficial) + finalizar */}
          <div className="flex items-center gap-2.5 lg:hidden">
            {showTimer && (
              <span
                className={`font-display text-xl font-bold tabular-nums ${timerColor} ${secs < 60 ? "animate-pulse" : ""}`}
                role="timer"
                aria-label={`Tempo restante: ${formatClock(secs)}`}
              >
                {formatClock(secs)}
              </span>
            )}
            <button
              type="button"
              onClick={() => setFinishModalOpen(true)}
              className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-red-700"
            >
              Finalizar
            </button>
          </div>
        </div>
        {/* Mobile: faixa de questões sempre visível */}
        <div className="border-t border-slate-100 px-4 py-2.5 lg:hidden">
          <QuestionNav
            questions={attempt.questions}
            answers={answers}
            flagged={flagged}
            current={current}
            onJump={goTo}
            variant="strip"
          />
        </div>
      </div>

      <div className="mx-auto flex max-w-5xl gap-6 px-4 py-6 lg:px-6">
        {/* Sidebar-guia (desktop) */}
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-24 flex flex-col gap-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              {showTimer && (
                <>
                  <p className="font-display text-xs font-semibold uppercase tracking-widest text-navy-400">
                    Tempo restante
                  </p>
                  <p
                    className={`font-display text-4xl font-bold leading-none tabular-nums ${timerColor} ${secs < 60 ? "animate-pulse" : ""}`}
                    role="timer"
                    aria-label={`Tempo restante: ${formatClock(secs)}`}
                  >
                    {formatClock(secs)}
                  </p>
                </>
              )}
              <p className={`text-sm text-navy-500 ${showTimer ? "mt-3" : ""}`}>
                <span className="font-semibold text-navy-800 tabular-nums">{answeredCount}</span> de {total} respondidas
              </p>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
                <div className="h-full rounded-full bg-navy-800 transition-all duration-300" style={{ width: `${progressPct}%` }} />
              </div>
              <div className="mt-5">
                <QuestionNav
                  questions={attempt.questions}
                  answers={answers}
                  flagged={flagged}
                  current={current}
                  onJump={goTo}
                  variant="grid"
                />
              </div>
              <div className="mt-5 flex flex-col gap-1.5 text-[11px] text-navy-400">
                <span className="inline-flex items-center gap-2"><span className="h-3 w-3 rounded bg-navy-800" /> respondida</span>
                <span className="inline-flex items-center gap-2"><span className="h-3 w-3 rounded bg-slate-200 ring-2 ring-gold-400" /> marcada p/ revisão</span>
                <span className="inline-flex items-center gap-2"><span className="h-3 w-3 rounded bg-slate-200" /> em branco</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setFinishModalOpen(true)}
              className="w-full rounded-xl bg-red-600 px-4 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-red-700 active:bg-red-800"
            >
              Finalizar simulado
            </button>
          </div>
        </aside>

        {/* Questão */}
        <main className="min-w-0 flex-1">
          <div ref={cardRef} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
            <div className="flex items-center justify-between gap-3">
              <p className="flex items-center gap-2 text-sm font-medium text-navy-500">
                <span>
                  Questão <span className="tabular-nums text-navy-800">{question.position}</span> de {total}
                </span>
                <span className="rounded-full bg-navy-50 px-2 py-0.5 text-xs font-medium text-navy-600">
                  {question.subject}
                </span>
                {question.repeated && (
                  <span
                    className="cursor-help rounded-full bg-gold-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-gold-700"
                    title="Você já respondeu esta questão recentemente — ela voltou pra fixar o conteúdo."
                  >
                    Reforço
                  </span>
                )}
              </p>
              <span className="text-xs text-navy-400" aria-live="polite">
                {saveState === "saving" && "salvando..."}
                {saveState === "saved" && "salvo ✓"}
                {saveState === "error" && <span className="text-red-600">erro — toque de novo</span>}
              </span>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                onClick={toggleFlag}
                aria-pressed={isFlagged}
                className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  isFlagged
                    ? "border border-gold-400 bg-gold-50 text-gold-600"
                    : "border border-slate-200 text-navy-500 hover:bg-slate-50"
                }`}
              >
                <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5" aria-hidden="true">
                  <path d="M3 2.75a.75.75 0 011.5 0v.508l6.026-1.004a1.75 1.75 0 011.94 1.13l.278.834 2.301.383A1.75 1.75 0 0116.5 6.327v5.11a1.75 1.75 0 01-1.462 1.726l-6.7 1.117A1.75 1.75 0 016.4 13.15l-.278-.833-1.622-.27v5.203a.75.75 0 01-1.5 0V2.75z" />
                </svg>
                {isFlagged ? "Marcada" : "Marcar p/ revisão"}
              </button>
              <button
                type="button"
                onClick={() => setAutoAdvance((v) => !v)}
                aria-pressed={autoAdvance}
                className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  autoAdvance
                    ? "border border-gold-400 bg-gold-50 text-gold-600"
                    : "border border-slate-200 text-navy-500 hover:bg-slate-50"
                }`}
              >
                <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5" aria-hidden="true">
                  <path d="M2 5.5a.75.75 0 011.19-.61l4.06 2.95a.75.75 0 010 1.22L3.19 12A.75.75 0 012 11.4V5.5zM10 5.5a.75.75 0 011.19-.61l4.06 2.95a.75.75 0 010 1.22L11.19 12a.75.75 0 01-1.19-.6V5.5z" />
                </svg>
                Avançar ao responder
              </button>
            </div>

            <QuestionCard
              mode="exam"
              statement={question.statement}
              alternatives={question.alternatives}
              referenceText={question.reference_text}
              selected={selected}
              onSelect={(i) => void selectAnswer(i)}
            />
            {selected !== null && (
              <p className="mt-3 text-xs text-navy-400">Toque na alternativa marcada para desmarcar.</p>
            )}

            <div className="mt-7 flex items-center justify-between border-t border-slate-100 pt-5">
              <button
                type="button"
                onClick={() => goTo(Math.max(0, current - 1))}
                disabled={current === 0}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-navy-600 transition-colors hover:bg-slate-100 disabled:opacity-40"
              >
                ← Anterior
              </button>
              {isLast ? (
                <button
                  type="button"
                  onClick={() => setFinishModalOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-navy-800 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-navy-900"
                >
                  Revisar e finalizar
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => goTo(Math.min(total - 1, current + 1))}
                  className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-navy-800 transition-colors hover:bg-slate-100"
                >
                  Próxima →
                </button>
              )}
            </div>
          </div>

          {secs < 300 && (
            <p
              className={`mt-4 rounded-xl border px-4 py-3 text-center text-sm font-medium ${
                secs < 60 ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-700"
              }`}
            >
              {secs < 60
                ? "Menos de 1 minuto! O simulado será finalizado automaticamente."
                : "Menos de 5 minutos restantes."}
            </p>
          )}
        </main>
      </div>

      {/* Modal de confirmação de finalização */}
      <Modal open={finishModalOpen} onClose={() => !finishing && setFinishModalOpen(false)} title="Finalizar simulado?">
        {unanswered.length > 0 ? (
          <div>
            <p className="text-sm text-navy-700">
              Você ainda tem{" "}
              <strong className="text-gold-600">
                {unanswered.length} {unanswered.length === 1 ? "questão sem resposta" : "questões sem resposta"}
              </strong>
              :
            </p>
            <p className="mt-2 max-h-24 overflow-y-auto text-sm text-navy-600 tabular-nums">
              {unanswered.map((q) => q.position).join(", ")}
            </p>
            <p className="mt-2 text-xs text-navy-400">Questões em branco contam como erro na nota.</p>
          </div>
        ) : (
          <p className="text-sm text-navy-700">Todas as {total} questões foram respondidas. Bom trabalho!</p>
        )}
        <div className="mt-5 flex flex-col gap-2">
          <Button block onClick={() => void finishAttempt(false)} disabled={finishing}>
            {finishing ? "Finalizando..." : "Finalizar e ver resultado"}
          </Button>
          <Button variant="ghost" block onClick={() => setFinishModalOpen(false)} disabled={finishing}>
            Voltar ao simulado
          </Button>
        </div>
      </Modal>
    </div>
  )
}
